import React, { SyntheticEvent, useState } from 'react';
import { Button, Checkbox, FormControlLabel } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';
import { useHistory } from 'react-router-dom';
import Link from '@material-ui/core/Link';
import { handleLogin } from '../../data/coreActions';
import { post } from '../../utils/ajax';
import { localRoutes, remoteRoutes } from '../../data/constants';
import Toast from '../../utils/Toast';
import XTextInput from '../../components/inputs/XTextInput';
import { useLoginStyles } from './loginStyles';
import GoogleButton, { GOOGLE_SIGN_IN_ENABLED } from './GoogleButton';
import { signInWithGoogle } from './googleSignIn';
import CheckIcon from '@material-ui/icons/Check';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import AuthLayout from './AuthLayout';

function Login() {
  const classes = useLoginStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [remember, setRemember] = useState(false);
  // Resetting a password is a detour from signing in, not a different
  // place to be, so it swaps the fields on this screen rather than
  // navigating away. Coming back is then just a state change and the
  // typed address is still here.
  const [mode, setMode] = useState<'login' | 'forgot' | 'code' | 'password'>(
    'login',
  );
  const [sentTo, setSentTo] = useState('');
  // Held between the two steps so the final call can re-send it. The
  // server checks it again there and never trusts this screen.
  const [verifiedCode, setVerifiedCode] = useState('');

  const schema = yup.object().shape({
    username: yup.string().email('Invalid email').required('Email is required'),
    password: yup.string().required('Password is required'),
  });

  const forgotSchema = yup.object().shape({
    username: yup.string().email('Invalid email').required('Email is required'),
  });

  const codeSchema = yup.object().shape({
    code: yup
      .string()
      .matches(/^\d{6}$/, 'Enter the 6-digit code from your email')
      .required('Code is required'),
  });

  const passwordSchema = yup.object().shape({
    password: yup
      .string()
      .min(8, 'Password must be at least 8 characters long')
      .required('Password is required'),
    // Typed blind, and not confirmed by signing in first, so a typo would
    // lock someone out of the account they are busy recovering.
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords do not match')
      .required('Please re-enter the password'),
  });

  const onSubmit = (data: any, actions: FormikHelpers<any>) => {
    post(
      remoteRoutes.login,
      { ...data, hubName: 'elevate' },
      (resp) => {
        dispatch(handleLogin(resp));
        Toast.success('Authentication success');
        history.push(localRoutes.dashboard);
      },
      (err: any, res: any) => {
        // An account that signed up once verification was required is
        // refused a session until the address is confirmed. Saying "invalid
        // username/password" there would be untrue and would leave them
        // with nowhere to go, so send them to the screen that can fix it.
        // HashRouter drops router state, so the email rides as a query
        // param, the same way RegisterForm does it.
        const body = res?.body ?? {};
        if (body.code === 'EMAIL_NOT_VERIFIED') {
          Toast.info(
            body.message ||
              'Confirm your email address to finish setting up your account.',
          );
          history.push(
            `${localRoutes.verifyEmail}?email=${encodeURIComponent(
              body.email || data.username,
            )}`,
          );
          actions.setSubmitting(false);
          return;
        }
        Toast.error('Authentication failed, invalid username/password');
        actions.setSubmitting(false);
      },
    );
  };

  function handleForgotPassword(e: SyntheticEvent<any>) {
    e.preventDefault();
    setMode('forgot');
  }

  function handleBackToLogin(e: SyntheticEvent<any>) {
    e.preventDefault();
    setMode('login');
  }

  const onForgotSubmit = (data: any, actions: FormikHelpers<any>) => {
    const done = () => {
      setSentTo(data.username);
      setMode('code');
      actions.setSubmitting(false);
    };
    // Success and failure land in the same place on purpose. Answering
    // differently would let a stranger use this box to find out which
    // addresses have accounts here.
    post(remoteRoutes.forgotPassword, { username: data.username }, done, done);
  };

  const onCodeSubmit = (data: any, actions: FormikHelpers<any>) => {
    post(
      remoteRoutes.passwordResetVerify,
      { username: sentTo, code: data.code },
      () => {
        setVerifiedCode(data.code);
        setMode('password');
        actions.setSubmitting(false);
      },
      (err: any, res: any) => {
        // The server distinguishes a wrong code from an expired one and
        // counts down the attempts left, so show its wording.
        Toast.error(
          res?.body?.message ||
            'That code is not valid or has expired. Please request a new one.',
        );
        actions.setSubmitting(false);
      },
    );
  };

  const onPasswordSubmit = (data: any, actions: FormikHelpers<any>) => {
    post(
      remoteRoutes.passwordResetComplete,
      { username: sentTo, code: verifiedCode, password: data.password },
      () => {
        // Every existing session is dropped server-side, so there is
        // nothing to carry over — they sign in fresh.
        Toast.success('Password changed. Please sign in.');
        actions.resetForm();
        setVerifiedCode('');
        setMode('login');
      },
      (err: any, res: any) => {
        Toast.error(
          res?.body?.message || 'Could not change the password. Try again.',
        );
        actions.setSubmitting(false);
      },
    );
  };

  function handleRegister(e: SyntheticEvent<any>) {
    e.preventDefault();
    history.push(localRoutes.register);
  }

  // Google proves who they are; it can't tell us the hub, course or phone we
  // still need, so a first-time Google user goes to the register form with
  // their verified details already filled in.
  const onGoogleCredential = (credential: string) => {
    signInWithGoogle(
      credential,
      (session) => {
        dispatch(handleLogin(session));
        Toast.success('Welcome back');
        history.push(localRoutes.dashboard);
      },
      (msg) => Toast.error(msg),
    );
  };

  if (mode === 'code') {
    return (
      <AuthLayout title="Enter your code" subtitle="Password reset">
        <div className={classes.formLabel}>
          A 6-digit code was sent to <strong>{sentTo}</strong>.
        </div>

        <Formik
          initialValues={{ code: '' }}
          validationSchema={codeSchema}
          onSubmit={onCodeSubmit}
        >
          {(formState) => (
            <Form className={classes.form}>
              <XTextInput
                name="code"
                label="6-digit code"
                autoComplete="one-time-code"
                autoFocus
                margin="normal"
                variant="outlined"
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={formState.isSubmitting}
                disableElevation
              >
                {formState.isSubmitting ? 'Checking…' : 'Continue'}
              </Button>
              <div className={classes.footerText}>
                <Link
                  className={`${classes.link} ${classes.linkAccent}`}
                  onClick={handleBackToLogin}
                >
                  Back to login
                </Link>
              </div>
            </Form>
          )}
        </Formik>
      </AuthLayout>
    );
  }

  if (mode === 'password') {
    return (
      <AuthLayout title="Set a new password" subtitle="Password reset">
        <div className={classes.formLabel}>
          Choose a new password for <strong>{sentTo}</strong>.
        </div>

        <Formik
          initialValues={{ password: '', confirmPassword: '' }}
          validationSchema={passwordSchema}
          onSubmit={onPasswordSubmit}
        >
          {(formState) => (
            <Form className={classes.form}>
              <XTextInput
                name="password"
                label="New password"
                autoComplete="new-password"
                autoFocus
                margin="normal"
                variant="outlined"
                isPassword
                startIcon={<LockOutlinedIcon fontSize="small" />}
              />
              <XTextInput
                name="confirmPassword"
                label="Confirm new password"
                autoComplete="new-password"
                margin="normal"
                variant="outlined"
                isPassword
                startIcon={<LockOutlinedIcon fontSize="small" />}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={formState.isSubmitting}
                disableElevation
              >
                {formState.isSubmitting ? 'Saving…' : 'Save new password'}
              </Button>
              <div className={classes.footerText}>
                <Link
                  className={`${classes.link} ${classes.linkAccent}`}
                  onClick={handleBackToLogin}
                >
                  Back to login
                </Link>
              </div>
            </Form>
          )}
        </Formik>
      </AuthLayout>
    );
  }

  if (mode === 'forgot') {
    return (
      <AuthLayout title="Forgot password?" subtitle="Password reset">
        <div className={classes.formLabel}>
          Enter your email address and we&apos;ll send you a 6-digit code.
        </div>

        <Formik
          initialValues={{ username: '' }}
          validationSchema={forgotSchema}
          onSubmit={onForgotSubmit}
        >
          {(formState) => (
            <Form className={classes.form}>
              <XTextInput
                type="email"
                name="username"
                label="Email address"
                autoComplete="off"
                autoFocus
                margin="normal"
                variant="outlined"
                startIcon={<MailOutlineIcon fontSize="small" />}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
                disabled={formState.isSubmitting}
                disableElevation
              >
                {formState.isSubmitting ? 'Sending…' : 'Send code'}
              </Button>
              <div className={classes.footerText}>
                Remembered it?{' '}
                <Link
                  className={`${classes.link} ${classes.linkAccent}`}
                  onClick={handleBackToLogin}
                >
                  Back to login
                </Link>
              </div>
            </Form>
          )}
        </Formik>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Welcome back" subtitle="Student's Portal">
      <div className={classes.formLabel}>Enter your details to login</div>

      <Formik
        initialValues={{ username: '', password: '' }}
        validationSchema={schema}
        onSubmit={onSubmit}
      >
        {(formState) => (
          <Form className={classes.form}>
            <XTextInput
              type="email"
              name="username"
              label="Email address"
              autoComplete="off"
              margin="normal"
              variant="outlined"
              startIcon={<MailOutlineIcon fontSize="small" />}
            />
            <XTextInput
              name="password"
              label="Password"
              autoComplete="off"
              margin="normal"
              variant="outlined"
              isPassword
              startIcon={<LockOutlinedIcon fontSize="small" />}
            />

            <div className={classes.rememberRow}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    disableRipple
                    className={classes.checkbox}
                    icon={<span className={classes.checkIcon} />}
                    checkedIcon={
                      <span className={classes.checkIconChecked}>
                        <CheckIcon
                          style={{
                            fontSize: 14,
                            stroke: 'currentColor',
                            strokeWidth: 1.5,
                          }}
                        />
                      </span>
                    }
                  />
                }
                label="Remember me"
              />
              <Link
                className={`${classes.link} ${classes.linkAccent}`}
                onClick={handleForgotPassword}
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={formState.isSubmitting}
              disableElevation
            >
              {formState.isSubmitting ? 'Signing in...' : 'Sign In'}
            </Button>

            {GOOGLE_SIGN_IN_ENABLED && (
              <GoogleButton
                onCredential={onGoogleCredential}
                text="continue_with"
                disabled={formState.isSubmitting}
              />
            )}

            <div className={classes.footerText}>
              Don&apos;t have an account?{' '}
              <Link
                className={`${classes.link} ${classes.linkAccent}`}
                onClick={handleRegister}
              >
                Sign up
              </Link>
            </div>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
}

export default Login;
